import { useState, useCallback } from 'react';
import { deleteLookup } from '../api/lookups';

interface UseDeleteLookupResult {
  deleteLookup: (id: number) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for deleting (deactivating) a lookup item
 */
export function useDeleteLookup(
  type: string,
  onSuccess?: () => void
): UseDeleteLookupResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteItem = useCallback(
    async (id: number): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        await deleteLookup(type, id);
        onSuccess?.();
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } } };
        const message = apiError.response?.data?.message || 'Failed to deactivate item';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [type, onSuccess]
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    deleteLookup: deleteItem,
    isLoading,
    error,
    reset,
  };
}
